/**
 * MaintenanceCartView — the page for an account-maintenance request (a cart with Mode = 'Maintenance';
 * Bill 2026-10-17: "Maintenance items, all or buildable from existing account opening parts").
 *
 * Before submit it explains the change in plain words: the account, what will change in Core Director, the forms and
 * who signs them, and any approval the bank's rule needs. For "Add a joint owner" the officer adds the person here
 * with the account-opening person flow (the account's existing owners are shown locked: they come from Core). Submit
 * runs the same two legs as account opening (signing setup when there is a form, then the commit), and from there the
 * opening screens take over: SignStep for the signatures, CommitFlow for progress and the finished screen.
 */
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle2, FileSignature, Wrench } from '@retail-ops/panel-shell/fi';
import { useNavigate } from 'react-router';
import type { CartDto, MaintenanceDescription } from '@retail-ops/shared-types';
import { ApiError } from '@retail-ops/api-client';
import { Button } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useApi } from '../hooks/useApi';
import { ensureSigningSessionForCart } from '../services/signingSetup';
import { PeopleStep } from '../steps/PeopleStep';
import { SignStep } from '../steps/SignStep';
import { CommitFlow } from '../CommitFlow';
import '@/components/maintenance/maintenance.css';

const STATUS: Record<string, { label: string; tone: '' | 'ok' | 'wait' }> = {
  draft: { label: 'Not submitted', tone: '' },
  active: { label: 'Not submitted', tone: '' },
  'awaiting-signatures': { label: 'Waiting for signatures', tone: 'wait' },
  committing: { label: 'Updating Core Director', tone: 'wait' },
  committed: { label: 'Done', tone: 'ok' },
  'pipeline-error': { label: 'Needs attention', tone: 'wait' },
  archived: { label: 'Closed', tone: '' },
};

const SIGNERS: Record<string, string> = {
  SIG_CARD: 'every owner on the account after the change',
  POD_DESIG: 'the account owners',
  POD_DESIG_CONTINGENT: 'the account owners',
  POD_DESIG_MINOR: 'the account owners',
  ODP_A9: 'the primary owner',
  ESIGN: 'nobody (it is given to the customer to read)',
};

function changeLines(d: MaintenanceDescription): string[] {
  const c = (d.changes ?? {}) as Record<string, unknown>;
  const owners = d.owners ?? [];
  const nameOf = (cif: unknown): string => owners.find((o) => o.cif === cif)?.name ?? String(cif ?? '');
  switch (d.action) {
    case 'debit-card': return [`Order a debit card for ${((c.cardholders as unknown[]) ?? []).map(nameOf).join(', ') || 'the chosen owners'}.`, 'Print and activate it on the finished screen.'];
    case 'order-checks': {
      const o = (c.order ?? {}) as Record<string, unknown>;
      return [`Order ${String(o.quantity ?? 60)} checks, design ${String(o.design ?? '')}, starting at #${String(o.startNumber ?? 1001)}.`, 'An Order checks task is created for the vendor order.'];
    }
    case 'pod': return ['Replace the POD beneficiaries with the designation entered (the current ones are removed first).'];
    case 'joint-add': return ['Add the new joint owner (below) to the account in Core Director, and to its owner fields.'];
    case 'joint-remove': return [`Remove ${String(c.removeName ?? nameOf(c.removeCif))} as a joint owner in Core Director, and clear their owner fields.`];
    case 'od-protection': return [`Set the Reg E overdraft choice to ${c.regE === 'optIn' ? 'opted in' : 'opted out'}.`];
    case 'od-limit': return [`Set the overdraft limit to $${String(c.newLimit ?? '')}.`];
    // CHG-0045 (Bill 2026-11-02): ROP does not change e-statements in Core. The officer gets a task to do it in the
    // Banno back office; the task closes itself once Core shows the change (Field 927 / 705).
    case 'statements': return [
      c.eStatements
        ? 'You get a task to enroll the customer in e-statements in the Banno back office (paper statements stop once enrolled).'
        : 'You get a task to remove the customer\'s e-statements enrollment in the Banno back office (paper statements resume).',
      'A note is added to the customer record in Core Director. The task closes itself once Core Director shows the change.',
    ];
    default: return [];
  }
}

export function MaintenanceCartView({ cart, onCartUpdated }: { cart: CartDto; onCartUpdated: () => void }): JSX.Element {
  const { client, makeSignal } = useApi();
  const { config } = useConfig();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [desc, setDesc] = useState<MaintenanceDescription | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'approve' | 'submit' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ceremonyActive, setCeremonyActive] = useState(false);
  const [resumeSigning, setResumeSigning] = useState(false);

  const load = useCallback(() => {
    const signal = makeSignal();
    client.retail.maintenance.get(cart.cartId, { signal })
      .then((d) => { setDesc(d); setLoadError(null); })
      .catch((e: unknown) => { if (!(e instanceof Error && e.name === 'AbortError')) setLoadError(e instanceof Error ? e.message : 'Could not load the request.'); });
  }, [client, cart.cartId, makeSignal]);
  useEffect(() => { load(); }, [load, cart.updatedAt, cart.status]);

  const docName = (code: string): string => {
    const hit = (config.disclosures as unknown as Array<Record<string, unknown>> | undefined)?.find((d) => d.code === code || d.templateCode === code);
    return (hit?.displayName as string) || (hit?.name as string) || code;
  };

  const ownerIds = (desc?.owners ?? []).map((o) => o.personId);
  let peopleCount = 0;
  try { const raw = JSON.parse(cart.peopleJson ?? '[]') as unknown; peopleCount = Array.isArray(raw) ? raw.length : 0; } catch { /* none */ }
  const newOwnerMissing = desc?.action === 'joint-add' && peopleCount <= ownerIds.length;
  const draft = cart.status === 'draft' || cart.status === 'active';
  const blocker = !desc ? null
    : !desc.enabled ? `${desc.title} has been switched off by an administrator, so it can't be submitted.`
    : desc.approvalRole && !desc.approvedBy ? `Needs approval from a ${desc.approvalRole} before it can be submitted.`
    : newOwnerMissing ? 'Add the new joint owner first.'
    : null;

  const approve = async (): Promise<void> => {
    setBusy('approve');
    setError(null);
    try { setDesc(await client.retail.maintenance.approve(cart.cartId)); }
    catch (e) { setError(ApiError.isApiError(e) || e instanceof Error ? (e as Error).message : 'Approval failed.'); }
    finally { setBusy(null); }
  };

  const submit = async (): Promise<void> => {
    if (!desc || blocker) return;
    setBusy('submit');
    setError(null);
    try {
      // Leg 1 (only when there is a form): the signing session, exactly as account opening prepares it. The server
      // chooses the documents for a maintenance cart (DisclosureRenderOrchestrator, maintenance branch).
      if (!desc.nothingToSign) {
        await ensureSigningSessionForCart({
          cart, client,
          config: { disclosures: config.disclosures, entityRules: config.entityRules, entityRequiredDocs: config.entityRequiredDocs, products: config.products },
          ...(user?.upn ? { fsrEmail: user.upn } : {}),
          ...(user?.displayName ? { fsrName: user.displayName } : {}),
          signal: makeSignal(),
        });
      }
      // Leg 2: the commit (no abort signal on a state-changing banking POST, as in opening).
      const outcome = await client.retail.carts.commit(cart.cartId);
      if (!outcome.success) { setError(outcome.error ?? 'The request could not be submitted.'); return; }
      onCartUpdated();
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : 'The request could not be submitted.');
    } finally {
      setBusy(null);
    }
  };

  const status = STATUS[cart.status] ?? { label: cart.status, tone: '' as const };
  return (
    <div className="rao-record">
      <div className="mt-view">
        <Button variant="ghost" size="sm" startIcon={<ArrowLeft size={14} />}
          onClick={() => navigate(desc?.startedFromCif ? `/rcm/${encodeURIComponent(desc.startedFromCif)}` : '/rao')}>
          Back to the customer
        </Button>

        <section className="mt-card">
          <div className="mt-head">
            <span className="mt-head__icon"><Wrench size={20} aria-hidden /></span>
            <div className="mt-head__text">
              <h1 className="mt-head__title">{desc?.title ?? 'Account maintenance'}</h1>
              <p className="mt-head__sub">
                {desc ? `Account ··${desc.acctId.slice(-4)} (${desc.productCode}) · ${(desc.owners ?? []).map((o) => o.name).join(', ')}` : 'Loading…'}
              </p>
            </div>
            <span className={`mt-status${status.tone ? ` mt-status--${status.tone}` : ''}`}>{status.label}</span>
          </div>
          {desc && !desc.coreProven && (
            <div className="mt-view__warn" role="note" style={{ marginTop: 'var(--sp-5)' }}>
              This change hasn't been proven against Core Director yet. Use test customers only until it has.
            </div>
          )}
          {loadError && <div className="mt-view__error" role="alert" style={{ marginTop: 'var(--sp-5)' }}>{loadError}</div>}
        </section>

        {desc && draft && (
          <>
            <section className="mt-card" aria-label="What will change">
              <h2 className="mt-card__title">What will change in Core Director</h2>
              <ul className="mt-steps">{changeLines(desc).map((l) => <li key={l}>{l}</li>)}</ul>
            </section>

            {desc.action === 'joint-add' && (
              <section className="mt-card" aria-label="The new joint owner">
                <h2 className="mt-card__title">The new joint owner</h2>
                <p className="mt-muted" style={{ marginBottom: 'var(--sp-4)' }}>
                  The account's current owners are shown from Core Director and can't be changed here. Add the new owner the same way as at account opening.
                </p>
                <PeopleStep cart={cart} onCartUpdated={() => onCartUpdated()} lockedPersonIds={ownerIds} />
              </section>
            )}

            <section className="mt-card" aria-label="Forms">
              <h2 className="mt-card__title">Forms</h2>
              {desc.nothingToSign ? <p className="mt-muted">Nothing to sign for this change.</p> : (
                <ul className="mt-steps">
                  {(desc.action === 'pod' ? ['POD_DESIG'] : desc.documents).map((code) => (
                    <li key={code}><FileSignature size={13} aria-hidden /> {docName(code)}: signed by {SIGNERS[code] ?? 'the owners'}</li>
                  ))}
                </ul>
              )}
              {!desc.nothingToSign && (
                <p className="mt-muted" style={{ marginTop: 'var(--sp-3)' }}>
                  Core Director is changed only after everyone has signed. Signers who aren't here get a signing link at the e-mail on their customer record.
                </p>
              )}
            </section>

            {desc.approvalRole && (
              <section className="mt-card" aria-label="Approval">
                <h2 className="mt-card__title">Approval</h2>
                <div className="mt-approval">
                  {desc.approvedBy ? (
                    <span><CheckCircle2 size={14} aria-hidden /> Approved by {desc.approvedBy}{desc.approvedAt ? ` on ${new Date(desc.approvedAt).toLocaleString()}` : ''}.</span>
                  ) : (
                    <>
                      <span>The bank's rule needs a {desc.approvalRole} to approve this before it is submitted{desc.requestedBy ? ` (requested by ${desc.requestedBy})` : ''}.</span>
                      {desc.canApprove && (
                        <Button variant="primary" onClick={() => void approve()} disabled={busy !== null}>
                          {busy === 'approve' ? 'Approving…' : `Approve as ${desc.approvalRole}`}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </section>
            )}

            {error && <div className="mt-view__error" role="alert">{error}</div>}
            <div className="mt-actions">
              <span className="mt-actions__hint">{blocker ?? (desc.nothingToSign ? 'Submitting updates Core Director now.' : 'Submitting prepares the forms for signing.')}</span>
              <Button variant="primary" onClick={() => void submit()} disabled={busy !== null || blocker !== null}>
                {busy === 'submit' ? 'Submitting…' : 'Submit change'}
              </Button>
            </div>
          </>
        )}

        {(cart.status === 'awaiting-signatures' || ((ceremonyActive || resumeSigning) && (cart.status === 'committing' || cart.status === 'committed'))) && (
          <SignStep cart={cart} onAllSigned={() => { setResumeSigning(false); onCartUpdated(); }} onCeremonyActiveChange={setCeremonyActive} />
        )}
        {(cart.status === 'awaiting-signatures' || cart.status === 'committing' || cart.status === 'committed' || cart.status === 'pipeline-error') && (
          <CommitFlow cart={cart} onCartUpdated={onCartUpdated}
            onExit={() => navigate(desc?.startedFromCif ? `/rcm/${encodeURIComponent(desc.startedFromCif)}` : '/rao')}
            onResumeSigning={() => setResumeSigning(true)} />
        )}
      </div>
    </div>
  );
}
